// frontend/src/pages/admin/AdminHistoryPage.tsx

import { useState, useEffect } from "react";
import { Search, ExternalLink, Clock, RefreshCw, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import * as api from "../../services/api";
import { format, subDays } from "date-fns";
import { fr, es, enUS as en, de, it, pt } from "date-fns/locale"; // 🆕
import { useTranslation } from "react-i18next"; // 🆕
import { useAuth } from "../../contexts/AuthContext";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { useQuery } from "@tanstack/react-query";

const mapDeliveryError = (error: unknown, t: (key: string) => string): string => {
  const data = (error as {
    response?: { data?: { code?: string; error?: string; details?: { message?: string } } };
  })?.response?.data;
  const code = data?.code;
  const serverMessage = data?.details?.message || data?.error;

  switch (code) {
    case "INTEGRATION_WHATSAPP_NOT_CONFIGURED":
      return t("admin.history.whatsappNotConfigured");
    case "INTEGRATION_EMAIL_NOT_CONFIGURED":
      return t("admin.history.emailNotConfigured");
    case "DELIVERY_CHANNEL_UNAVAILABLE":
      return t("admin.history.channelUnavailable");
    case "CLIENT_EMAIL_MISSING":
      return t("admin.history.clientEmailMissing");
    case "INVOICE_NOT_FOUND":
      return t("admin.history.invoiceNotFound");
    case "INVOICE_PDF_NOT_FOUND":
      return t("admin.history.invoicePdfNotFound");
    case "DELIVERY_FAILED":
    case "INTERNAL_ERROR":
      return serverMessage || t("admin.history.resendFailed");
    default:
      return serverMessage || t("admin.history.resendFailed");
  }
};

export default function AdminHistoryPage() {
  const { t, i18n } = useTranslation();
  const { artist, platformCapabilities } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [resending, setResending] = useState<string | null>(null);
  const [activeDate, setActiveDate] = useState<string>("");
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
  });
  const [isFreePlan, setIsFreePlan] = useState(false);

  const artistId = artist?.id || "1";

  const { data: integrations } = useQuery({
    queryKey: ["integrations", artistId],
    queryFn: () => api.getIntegrations(artistId),
    enabled: !!artistId,
  });

  // Récupère le plan pour appliquer la limite 7 jours (Free)
  useEffect(() => {
    api.getBillingStatus()
      .then((s) => setIsFreePlan(s.plan === "FREE"))
      .catch(() => setIsFreePlan(false));
  }, []);

  const canSendEmail =
    platformCapabilities.email || integrations?.channelStatus?.email === true;
  const canSendWhatsapp =
    platformCapabilities.whatsapp || integrations?.channelStatus?.whatsapp === true;

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await api.getHistory(artistId);
      setHistory(data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (invoiceId: string, channel: string) => {
    if (!invoiceId) return;
    setResending(invoiceId);
    try {
      await api.resendInvoice(invoiceId, channel);
      setModalState({
        isOpen: true,
        title: `✅ ${t("common.success")}`,
        message:
          channel === "WHATSAPP"
            ? t("admin.history.resendWhatsappSuccess")
            : t("admin.history.resendEmailSuccess"),
      });
      fetchHistory();
    } catch (error) {
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message: mapDeliveryError(error, t),
      });
    } finally {
      setResending(null);
    }
  };

  const sevenDaysAgo = subDays(new Date(), 7);

  const filteredHistory = history.filter(item => {
    const completedAt = new Date(item.completedAt);
    // Limite 7 jours pour le plan Free
    if (isFreePlan && completedAt < sevenDaysAgo) return false;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.email.toLowerCase().includes(searchTerm.toLowerCase());
    const itemDate = format(completedAt, "yyyy-MM-dd");
    const matchesDate = activeDate ? itemDate === activeDate : true;
    return matchesSearch && matchesDate;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "SENT": return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "FAILED": return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
        <p className="text-gray-600 ml-3">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <button
        onClick={() => navigate("/admin/dashboard")}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-4 text-sm"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t("common.backToDashboard")}
      </button>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{t("admin.history.title")}</h1>

        {isFreePlan && (
          <div className="flex items-center gap-2 border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600">
            <span>{t("admin.history.freeLimitBanner", "Historique limité aux 7 derniers jours.")}</span>
            <Link to="/admin/billing" className="font-medium underline hover:text-gray-900">
              {t("admin.billing.upgradeToPro", "Passer à Pro")}
            </Link>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t("common.searchPlaceholder") || "Client, email..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-200 focus:border-gray-900 outline-none w-48 text-sm"
            />
          </div>
          <input
            type="date"
            value={activeDate}
            onChange={(e) => setActiveDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 focus:border-gray-900 outline-none text-sm"
          />
          <button
            onClick={() => {setSearchTerm(""); setActiveDate("");}}
            className="p-2 border border-gray-200 hover:bg-gray-50 text-gray-400"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t("admin.history.clientAndDate")}</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t("admin.history.session")}</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t("admin.history.payment")}</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t("admin.history.delivery")}</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t("admin.history.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredHistory.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{item.name}</div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-tighter">
                        {format(new Date(item.completedAt), "dd MMMM yyyy", {
                           locale: ({ fr, es, en, de, it, pt } as any)[i18n.language.split("-")[0]] || fr
                        })}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {item.actualDuration || 0} {t("common.minutes")}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-gray-900 text-sm">
                      {(item.amountPaid || 0).toFixed(2)} €
                      <span className="ml-2 text-[9px] uppercase text-gray-500">
                        {item.paymentMethod || "Cache"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                       {getStatusIcon(item.invoice?.deliveryStatus || "PENDING")}
                       <span className="text-xs text-gray-500 uppercase">
                          {item.deliveryMethod === "NONE" ? (t("common.none") || "Aucun") : item.deliveryMethod}
                       </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                       {item.receiptPdfUrl && (
                         <a href={item.receiptPdfUrl} target="_blank" className="p-2 text-gray-700 hover:bg-gray-50" title={t("admin.history.viewPdf")}>
                            <ExternalLink className="w-4 h-4" />
                         </a>
                       )}
                       {item.status === "COMPLETED" && item.invoice && (
                         <div className="flex gap-1">
                           <button 
                             disabled={!!resending || !canSendEmail}
                             onClick={() => handleResend(item.invoice.id, "EMAIL")}
                             className="text-[10px] px-2 py-1 border border-gray-200 rounded hover:bg-gray-100 transition-all font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                             title={!canSendEmail ? t("admin.history.emailNotConfigured") : undefined}
                           >
                             {t("admin.history.resendEmail") || "Email"}
                           </button>
                           <button 
                             disabled={!!resending || !canSendWhatsapp}
                             onClick={() => handleResend(item.invoice.id, "WHATSAPP")}
                             className="text-[10px] px-2 py-1 border border-gray-200 rounded hover:bg-gray-100 transition-all font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                             title={!canSendWhatsapp ? t("admin.history.whatsappNotConfigured") : undefined}
                           >
                             {t("admin.history.resendWA") || "WA"}
                           </button>
                         </div>
                       )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
      />
    </div>
  );
}
